import { useEffect, useState } from 'react';
import ErrorUtils from "src/utils/ErrorUtils";
import { parseExcelFromArrayBuffer, ExcelRowConverter } from "src/utils/ExcelUtils";

/**
 * MDA Results row data with simplified array-based structure
 */
export interface MDAResultsRow {
    row: (string | number | undefined)[];
}

export interface ExcelHeaderData {
    value: string;
}

/**
 * Simplified converter for MDA results data
 */
const mdaResultsConverter: ExcelRowConverter<MDAResultsRow> = (rowData: any[], headers: string[], rowIndex: number): MDAResultsRow => {
    // Convert row data to simplified array format
    const row: (string | number | undefined)[] = headers.map((header, columnIndex) => {
        const cellValue = rowData[columnIndex];
        
        // Handle different data types
        if (cellValue === null || cellValue === undefined) {
            return undefined;
        }
        
        return cellValue;
    });

    return {
        row
    };
};

/**
 * MDA Results-specific Excel data hook with simplified data structure
 * 
 * @param preSignedUrl - The pre-signed URL to fetch the Excel file from
 * @returns Object containing excel data, headers, loading state, and error message
 */
export const useMDAResultsData = (preSignedUrl?: string) => {
    const [excelData, setExcelData] = useState<MDAResultsRow[]>([]);
    const [headers, setHeaders] = useState<ExcelHeaderData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!preSignedUrl) {
            return;
        }

        const fetchAndParseExcel = async () => {
            setLoading(true);
            setError('');
            
            try {
                // Fetch the Excel file from the preSignedUrl
                const response = await fetch(preSignedUrl);
                if (!response.ok) {
                    throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                }

                // Get the file as array buffer
                const arrayBuffer = await response.arrayBuffer();
                
                // Parse Excel using MDA Results-specific converter
                const parseResult = parseExcelFromArrayBuffer<MDAResultsRow>(
                    arrayBuffer,
                    mdaResultsConverter,
                    {   // parser options:
                        defaultValue: "",
                        sheetIndex: 1
                    }
                );
                
                if (parseResult.headers.length === 0) {
                    setExcelData([]);
                    setHeaders([]);
                    return;
                }

                console.log("MDA Results data objects", parseResult.data);

                // Transform headers to simple format (no validation needed)
                const headerData: ExcelHeaderData[] = parseResult.headers.map((headerValue, columnIndex) => {
                    return {
                        value: headerValue
                    };
                });

                setHeaders(headerData);
                setExcelData(parseResult.data);
            } catch (err) {
                setError(ErrorUtils.getMessage(err));
            } finally {
                setLoading(false);
            }
        };

        fetchAndParseExcel();
    }, [preSignedUrl]);

    return {
        excelData,
        headers,
        loading,
        error
    };
};
